import BranchRequiredWrapper from "@/components/banners/BranchRequiredWrapper";
import { Card, CardContent, CardHeader } from "@/components/layouts/Card";
import DataTable, { Column } from "@/components/layouts/DataTable";
import PageLoader from "@/components/loader/PageLoader";
import SmallSpinner from "@/components/loader/SmallSpinner";
import { useUser } from "@/contexts/UserContext";
import { useAxiosInstance } from "@/utils/useAxiosInstance";
import { faEdit, faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import PrinterHeader from "./PrinterHeader";
import DeleteConfirmationDialog from "@/components/layouts/DeleteConfirmationDialog";

interface Printer {
  id: number;
  name: string;
  is_primary: boolean;
  is_report_printer: boolean;
  printer_id: number;
  state: string;
}
interface Computer {
  id: number;
  computer_id: number;
  name: string;
  state: string;
  last_seen: string;
}

interface SortState {
  field: keyof Printer;
  direction: "asc" | "desc";
}

interface FilterState {
  state: string;
}

interface DeleteItem {
  id: number | string;
  type: 'printer' | 'computer';
}

export default function PrinterTable() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const itemsPerPage = 10;
  const [printers, setPrinters] = useState<Printer[]>([]);
  const [computers, setComputers] = useState<Computer[]>([]);
  const [isComputerStatusLoading, setIsComputerStatusLoading] = useState(false);
  const prevComputersRef = useRef<Computer[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortState, setSortState] = useState<SortState>({
    field: "name",
    direction: "asc",
  });
  const { user } = useUser();
  const [axiosInstance, loading, isFetching] = useAxiosInstance();
  const [filterStartDate, setFilterStartDate] = useState<Date | null>(null);
  const [filterEndDate, setFilterEndDate] = useState<Date | null>(null);
  const [filterState, setFilterState] = useState<FilterState>({
    state: "",
  });
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<DeleteItem | null>(null);

  const columns: Column<Printer>[] = [
    {
      key: "name",
      header: t("back.management.printer.table.name"),
      sortable: true,
      bold: true,
    },
    {
      key: "is_primary",
      header: t("back.management.printer.table.isDefault"),
      sortable: true,
      render: (printer: Printer) => (printer.is_primary ? "Yes" : "No"),
    },
    {
      key: "is_report_printer",
      header: t("back.management.printer.table.isReportPrinter"),
      sortable: true,
      render: (printer: Printer) => (printer.is_report_printer ? "Yes" : "No"),
    },
    {
      key: "printer_id",
      header: t("back.management.printer.table.printerId"),
      sortable: true,
    },
    {
      key: "state",
      header: t("back.management.printer.table.state"),
      sortable: true,
    },
    {
      key: "action",
      header: t("back.management.menu.product.table.action"),
      render: (printer: Printer) => (
        <Link to={`/printer/${printer.id}`} className="mr-2">
          <FontAwesomeIcon
            icon={faEdit}
            className="fa-2x text-gray-600 hover:text-gray-800"
          />
        </Link>
      ),
    },
  ];

  const getStatusColor = (printer: Printer) => {
    return printer.state === "online" ? "bg-green-500" : "bg-gray-500";
  };

  useEffect(() => {
    fetchPrinters();
  }, [
    currentPage,
    searchQuery,
    sortState,
    filterState,
    filterStartDate,
    filterEndDate,
  ]);

  const fetchPrinters = useCallback(async () => {
    try {
      // Check readiness inside the try block
      if (!axiosInstance || !user?.selectedBranch || loading) {
        // If conditions are not met, simply return without logging
        return;
      }

      // Proceed with fetching logic if ready
      let url = `print/printers/?branch_id=${user.selectedBranch.id}&page=${currentPage}&page_size=${itemsPerPage}`;
      if (searchQuery) url += `&name=${encodeURIComponent(searchQuery)}`;
      if (filterState.state) url += `&state=${filterState.state}`;

      const response = await axiosInstance.get(url);
      if (response.status === 200) {
        setPrinters(response.data.results);
        setTotalItems(response.data.count);
      } else {
        throw new Error("Failed to fetch printers");
      }
    } catch (error) {
      console.error("Error fetching printers:", error);
      setPrinters([]);
      toast.error("Failed to fetch printers.");
    }
  }, [
    axiosInstance,
    user?.selectedBranch,
    currentPage,
    itemsPerPage,
    searchQuery,
    filterState.state,
    loading,
  ]);

  useEffect(() => {
    fetchPrinters();
  }, [fetchPrinters]);

  const fetchComputerStatus = useCallback(async () => {
    try {
      // Check readiness inside the try block
      if (!axiosInstance || !user?.selectedBranch || loading) {
        // If conditions are not met, simply return without logging
        return;
      }

      setIsComputerStatusLoading(true);
      const response = await axiosInstance.get(
        `print/branch/status/?branch_id=${user.selectedBranch.id}`,
      );

      if (response.status === 200) {
        const newComputers = response.data.computers;
        // Compare with previous data to determine if updates are needed
        if (
          JSON.stringify(newComputers) !==
          JSON.stringify(prevComputersRef.current)
        ) {
          setComputers(newComputers);
          prevComputersRef.current = newComputers;
        }
      } else {
        throw new Error("Failed to fetch computer status");
      }
    } catch (error) {
      console.error("Error fetching computer status:", error);
    } finally {
      setIsComputerStatusLoading(false);
    }
  }, [axiosInstance, user?.selectedBranch, loading]);

  useEffect(() => {
    fetchComputerStatus(); // Fetch immediately on mount
    const intervalId = setInterval(fetchComputerStatus, 60000); // Poll every minute

    return () => clearInterval(intervalId);
  }, [fetchComputerStatus]);

  const renderComputerStatusCard = () => (
    <Card className="relative mb-6">
      <CardHeader className="font-semibold">
        {t("back.management.printer.computerStatus")}
      </CardHeader>
      <CardContent>
        {isComputerStatusLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75">
            <SmallSpinner />
          </div>
        )}
        {computers.map((computer) => (
          <div
            key={computer.computer_id}
            className="mb-3 flex items-center justify-between border-b border-gray-100 pb-2 last:border-b-0"
          >
            <div className="flex items-center gap-3">
              <div 
                className={`h-3 w-3 rounded-full ${
                  computer.state === "connected" ? "bg-green-500" : "bg-red-500"
                }`}
                title={computer.state}
              />
              <span className="text-gray-700">{computer.name}</span>
            </div>
            <button 
              onClick={() => openDeleteConfirmation(computer.id, 'computer')}
              className="text-red-600 hover:text-red-800 p-2 transition-colors duration-200"
            >
              <FontAwesomeIcon 
                icon={faTrashAlt} 
                className="fa-2x" 
                title={t("back.management.computer.delete")}
              />
            </button>
          </div>
        ))}
      </CardContent>
    </Card>
  );

  const handleSortChange = (field: keyof Printer) => {
    setSortState((prevState) => ({
      field,
      direction:
        prevState.field === field && prevState.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const setupPrinters = async () => {
    try {
      if (!axiosInstance || !user?.selectedBranch) {
        console.error("Axios instance or selected branch not available");
        return;
      }

      const response = await axiosInstance.post("/print/setup_printers/", {
        branch_id: user.selectedBranch.id,
      });

      if (response.status === 200) {
        toast.success("Printers set up successfully");
        fetchPrinters();
      } else {
        throw new Error(
          "Please make sure you have the latest client downloaded and logged in with the provided credentials.",
        );
      }
    } catch (error) {
      console.error("Error setting up printers:", error);
      toast.error(
        "Please make sure you have the latest client downloaded and logged in with the provided credentials.",
      );
    }
  };

  const handleSetupPrinters = async () => {
    try {
      await setupPrinters();
    } catch (error: any) {
      if (error.response && error.response.status === 401) {
        toast.error(
          "Please make sure you have the latest client downloaded and logged in with the provided credentials.",
        );
      } else {
        console.error("Error setting up printers:", error);
        toast.error(
          "Please make sure you have the latest client downloaded and logged in with the provided credentials.",
        );
      }
    }
  };

  const openDeleteConfirmation = (id: number | string, type: 'printer' | 'computer') => {
    setItemToDelete({ id, type });
    setDeleteConfirmationOpen(true);
  };

  const closeDeleteConfirmation = () => {
    setItemToDelete(null);
    setDeleteConfirmationOpen(false);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;

    try {
      if (!axiosInstance || !user?.selectedBranch) {
        toast.error("Not authorized to perform this action");
        return;
      }

      const endpoint = itemToDelete.type === 'printer' 
        ? `/print/printers/${itemToDelete.id}/`
        : `/print/computers/${itemToDelete.id}/`;

      const response = await axiosInstance.delete(endpoint);
      
      if (response.status === 204 || response.status === 200) {
        toast.success(t(`back.management.${itemToDelete.type}.deleteSuccess`));
        if (itemToDelete.type === 'printer') {
          await fetchPrinters();
        } else {
          await fetchComputerStatus();
        }
      } else {
        throw new Error(`Failed to delete ${itemToDelete.type}`);
      }
    } catch (error) {
      console.error(`Error deleting ${itemToDelete.type}:`, error);
      toast.error(t(`back.management.${itemToDelete.type}.deleteError`));
    } finally {
      closeDeleteConfirmation();
    }
  };

  const renderMobileCard = (printer: Printer) => (
    <div className="flex w-full items-center justify-between">
      <div className="flex flex-col">
        <p className="font-bold text-gray-900">{printer.name}</p>
        <p className="text-gray-700">
          Primary: {printer.is_primary ? "Yes" : "No"}
        </p>
        <p className="text-gray-700">
          Report Printer: {printer.is_report_printer ? "Yes" : "No"}
        </p>
        <p className="text-gray-700">ID: {printer.printer_id}</p>
        <p className="text-gray-700">State: {printer.state}</p>
      </div>
      <div className="flex h-full flex-col justify-between">
        <Link to={`/printer/${printer.id}`} className="mb-2">
          <FontAwesomeIcon
            icon={faEdit}
            className="fa-2x text-gray-600 hover:text-gray-800"
          />
        </Link>
        <button onClick={() => openDeleteConfirmation(printer.id, 'printer')}>
          <FontAwesomeIcon
            icon={faTrashAlt}
            className="fa-2x text-red-600 hover:text-red-800"
          />
        </button>
      </div>
    </div>
  );

  return (
    <PageLoader isFetching={isFetching}>
      <BranchRequiredWrapper>
        <div className="container mx-auto px-4 py-2">
          <PrinterHeader onSetupPrinters={handleSetupPrinters} />

          {renderComputerStatusCard()}

          <DeleteConfirmationDialog
            isOpen={deleteConfirmationOpen}
            onClose={closeDeleteConfirmation}
            onConfirm={confirmDelete}
            title={`Delete ${itemToDelete?.type === 'printer' ? 'Printer' : 'Computer'}`}
            message={`Are you sure you want to delete this ${itemToDelete?.type === 'printer' ? 'printer' : 'computer'}?`}
          />

          <DataTable
            data={printers}
            columns={columns}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            totalItems={totalItems}
            sortState={sortState}
            onSortChange={handleSortChange}
            onPageChange={handlePageChange}
            renderMobileCard={renderMobileCard}
            onEdit={(id) => navigate(`/printer/${id}`)}
            onDelete={(id) => openDeleteConfirmation(id, 'printer')}
            getStatusColor={getStatusColor}
          />
        </div>
      </BranchRequiredWrapper>
    </PageLoader>
  );
}
